import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { ChevronRightIcon, ChevronLeftIcon, CheckCircleIcon, ExclamationIcon } from '@heroicons/react/solid';
import Breadcrumb from '../../../components/Breadcrumb';
import CISideNavbar from './components/CISideNavbar';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import AutoSaveCreatable from '../../../components/AutoSaveCreatable';
import DictationTextArea from '../../../components/DictationTextArea';
import ModuleCompletionDialog, { CancelButton, PrimaryActionButton } from '../../../components/ModuleCompletionDialog';
import CIEndDecisionDialog from '../../../components/CIEndDecisionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { auth, db } from '../../../firebase';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { isClinicianAssignedToStage } from '../../../utils/clinicianPermissions';
import { buildFirstGeneratedFields, buildFirstCompletedFields, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import {
  buildEndAssessmentUpdates,
  buildProceedToSsiUpdates,
  ASSESSMENT_CONCLUSION_SECTION_TITLE,
  ASSESSMENT_END_AUDIT_EVENTS,
} from '../../../utils/assessmentEnd';
import { CI_END_DECISIONS } from '../../../constants/workflowConfig';
import { useAuth } from '../../../hooks/useAuth';
import { buildCIExclusionsContext, getVisibleCISections, getVisibleCISectionTitles, getPrevNextCISectionKeys, mapCIDisplayTitle } from '../../../utils/clinicalInterviewWorkflow';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import { buildMergedSelection, splitPredefinedAndCustom } from '../../../utils/selectionMergeUtils';

function RiskAssessment() {

  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, fetchPatientData, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  const currentSectionNumber = 11;
  const currentStageTitle = 'Clinical Interview';
  const currentSectionTitle = 'Risk Assessment';
  const prevSectionLink = '/proforma/clinical-interview/mental-status-examination';
  const prevSectionTitle = 'Mental State Examination';
  const nextSectionLink = '/proforma/clinical-interview/diagnostic-formulation';
  const nextSectionTitle = mapCIDisplayTitle('Diagnostic Formulation', wf);

  const [canEdit, setCanEdit] = useState(false);

  // Check if the current clinician is assigned to this stage
  useEffect(() => {
    const checkPermissions = async () => {
      const currentUser = auth.currentUser;
      if (currentUser && patientDataModel) {
        const hasEditPermission = isClinicianAssignedToStage(patientDataModel, currentUser.uid, 'CI');
        setCanEdit(hasEditPermission);
      }
    };
    
    checkPermissions();
  }, [patientDataModel]);

  // const questionPanels = useMemo(() => [
  //   {
  //     title: "11.1 Risks to Self",
  //     categories: {
  //       'Self-harm ideation': { yesOptions: ['Passive', 'Active with Plan', 'Active with Intent', 'Active with Means'], noOptions: ['None'] },
  //       'Suicidal ideation': { yesOptions: ['Passive', 'Active with Plan', 'Active with Intent', 'Active with Means'], noOptions: ['None'] },
  //     },
  //   },
  //   {
  //     title: "11.2 Risks to Others",
  //     categories: {
  //       'Risk to Others': { yesOptions: ['Passive', 'Active with Plan', 'Active with Intent', 'Active with Means'], noOptions: ['None'] },
  //     },
  //   },
  //   {
  //     title: "11.3 Other Risks",
  //     categories: {
  //       'Self-neglect': { yesOptions: ['Mild', 'Moderate', 'Severe'], noOptions: ['None'] },
  //       'Substance abuse': { yesOptions: ['Mild', 'Moderate', 'Severe'], noOptions: ['None'] },
  //       'Harm from others': { yesOptions: ['Mild', 'Moderate', 'Severe'], noOptions: ['None'] },
  //     },
  //   },
  // ], []);

  const questionPanels = useMemo(() => [
    {
      title: "11.1 Risks to Self",
      categories: {
        'Self-harm ideation': { yesOptions: ['Passive', 'Active with Plan but no Intent', 'Active with Intent but no Means', 'Active with Means and Intent', 'Active with Means but no Intent'], noOptions: ['None'] },
        'Suicidal ideation': { yesOptions: ['Passive', 'Active with Plan but no Intent', 'Active with Intent but no Means', 'Active with Means and Intent', 'Active with Means but no Intent'], noOptions: ['None'] },
      },
    },
    {
      title: "11.2 Risks to Others",
      categories: {
        'Risk to Others': { yesOptions: ['Passive', 'Active with Plan but no Intent', 'Active with Intent but no Means', 'Active with Means and Intent', 'Active with Means but no Intent'], noOptions: ['None'] },
      },
    },
    {
      title: "11.3 Other Risks",
      categories: {
        'Self-neglect': { yesOptions: ['Mild', 'Moderate', 'Severe'], noOptions: ['None'] },
        'Substance abuse': { yesOptions: ['Mild', 'Moderate', 'Severe'], noOptions: ['None'] },
        'Harm from others': { yesOptions: ['Mild', 'Moderate', 'Severe'], noOptions: ['None'] },
      },
    },
  ], []);


  const organisationName = claims?.organisation || 'default';

  const { sections: ciSections, excludedKeys } = buildCIExclusionsContext(patientDataModel, organisationName);
  const SECTIONS_CI = useMemo(() => getVisibleCISections(ciSections, excludedKeys), [ciSections, excludedKeys]);
  const currentSectionIndex = useMemo(() => SECTIONS_CI.findIndex(s => s.key === '11'), [SECTIONS_CI]);
  const visibleKeys = useMemo(() => SECTIONS_CI.map(s => s.key), [SECTIONS_CI]);
  const { prevKey } = useMemo(() => getPrevNextCISectionKeys(visibleKeys, '11'), [visibleKeys]);
  const prevRoute = useMemo(() => {
    const prevSection = SECTIONS_CI.find(s => s.key === prevKey);
    return prevSection?.route || prevSectionLink;
  }, [SECTIONS_CI, prevKey, prevSectionLink]);
  const prevTitleComputed = useMemo(() => {
    const prevSection = SECTIONS_CI.find(s => s.key === prevKey);
    const baseTitle = prevSection?.sectionTitle || prevSectionTitle;
    return mapCIDisplayTitle(baseTitle, wf);
  }, [SECTIONS_CI, prevKey, prevSectionTitle, wf]);

  const [initialized, setInitialized] = useState(false);

  const initializeSelections = useCallback((sectionData) => {
    const subsections = sectionData?.subsections || {};
    return questionPanels.map((panel, panelIndex) =>
      Object.entries(panel.categories).reduce((acc, [categoryName, category]) => {
        const categoryData = subsections[panelIndex + 1]?.categories?.[categoryName] || {};
        acc[categoryName] = {
          present: categoryData.isPresentSelection ?? null,
          selectedOption: (categoryData.selectionDetails || []).map(option => ({ value: option, label: option }))
        };
        return acc;
      }, {})
    );
  }, [questionPanels]);

  const [localData, setLocalData] = useState(() => {
    const sectionData = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[currentSectionNumber] || {};
    return {      
      selections: initializeSelections(sectionData)
    };
  });

  // Add this state for additional notes
  const [additionalNotes, setAdditionalNotes] = useState('');


  // Replace the existing useEffect with one-time initialization
  useEffect(() => {
    if (!initialized && patientDataModel?.dataModel) {
      const sectionData = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[currentSectionNumber] || {};
      setLocalData({        
        selections: initializeSelections(sectionData)
      });
      
      // Initialize additional notes
      setAdditionalNotes(sectionData.clinicianNotes?.['Additional Notes'] || '');
    
      setInitialized(true);
    }
  }, [patientDataModel, currentSectionNumber, initializeSelections, initialized]);


  // Update section updates to be conditional
  const sendSectionUpdates = useCallback((newUpdates) => {
    if (!canEdit) {
      console.warn('Update blocked: Clinician does not have edit permission for this stage');
      return Promise.resolve();
    }
    
    return updatePatientFirestore(patientId, {
      '2_clinicalInterview': {
        sections: {
          [currentSectionNumber]: newUpdates
        }
      }
    });
  }, [patientId, currentSectionNumber, updatePatientFirestore, canEdit]);

  const [hasChanges, setHasChanges] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: currentStageTitle,
    sectionTitle: currentSectionTitle,
    hasChanges,
    canEdit,
    patientDataModel,
  });

  // No confirmation dialog needed for RSK — custom (clinician-typed) dropdown
  // entries are preserved by buildMergedSelection when toggling Yes/No, and RSK
  // has no subcategory trees that would be lost.  Only predefined options (which
  // are swappable defaults) change on toggle.

  const handleAdditionalNotesChange = (value) => {
    if (!canEdit) return;

    setAdditionalNotes(value);
    setHasChanges(true);
  };
  
  const handleAdditionalNotesBlur = () => {
    if (!canEdit) return;

    sendSectionUpdates({
      clinicianNotes: {
        'Additional Notes': additionalNotes
      }
    });
  };

  // Core toggle logic — when toggling Yes/No, predefined dropdown options are
  // replaced with the appropriate default (e.g. "None" for No), but any custom
  // (clinician-typed) free-text entries are preserved via buildMergedSelection.
  const executeSelection = (panelIndex, categoryName, isPresent) => {
    const category = questionPanels[panelIndex].categories[categoryName];
    const defaultValue = isPresent === false ? category.noOptions[0] : null;
    const existingSelections = localData.selections[panelIndex]?.[categoryName]?.selectedOption || [];
    const newSelectedOption = buildMergedSelection(
      existingSelections, category.yesOptions || [], category.noOptions || [], defaultValue
    );

    setLocalData(prev => {
      const newSelections = [...prev.selections];
      newSelections[panelIndex] = {
        ...newSelections[panelIndex],
        [categoryName]: {
          present: isPresent,
          selectedOption: newSelectedOption
        },
      };
      return { ...prev, selections: newSelections };
    });

    sendSectionUpdates({
      subsections: {
        [panelIndex + 1]: {
          categories: {
            [categoryName]: {
              isPresentSelection: isPresent,
              selectionDetails: newSelectedOption.map(o => o.value)
            }
          }
        }
      }
    });

    setHasChanges(true);
  };

  // Toggle handler — clicking the already-active button means "unselect"
  // (reset to null).  No confirmation dialog is needed because custom entries
  // are preserved by buildMergedSelection, and RSK has no subcategories.
  const handleSelection = (panelIndex, categoryName, isPresent) => {
    if (!canEdit) return;

    const currentPresent = localData.selections[panelIndex]?.[categoryName]?.present;
    const newValue = currentPresent === isPresent ? null : isPresent;
    executeSelection(panelIndex, categoryName, newValue);
  };

  const handleOptionChange = (panelIndex, categoryName, selectedOptions) => {
    if (!canEdit) return;

    setLocalData(prev => {
      const newSelections = [...prev.selections];
      newSelections[panelIndex] = {
        ...newSelections[panelIndex],
        [categoryName]: {
          ...newSelections[panelIndex][categoryName],
          selectedOption: selectedOptions,
          present: newSelections[panelIndex][categoryName].present,
        },
      };
      return { ...prev, selections: newSelections };
    });

    sendSectionUpdates({
      subsections: {
        [panelIndex + 1]: {
          categories: {
            [categoryName]: {
              isPresentSelection: localData.selections[panelIndex][categoryName].present,
              selectionDetails: selectedOptions.map(option => option.value)
            }
          }
        }
      }
    });

    setHasChanges(true);
  };

  // Core "No to all" logic — sets every category in the panel to "No" while
  // preserving any custom (clinician-typed) entries via buildMergedSelection.
  const executeNoToAll = (panelIndex) => {
    setLocalData(prev => {
      const newSelections = [...prev.selections];
      newSelections[panelIndex] = Object.entries(questionPanels[panelIndex].categories).reduce((acc, [categoryName, category]) => {
        const existingSelections = prev.selections[panelIndex]?.[categoryName]?.selectedOption || [];
        const merged = buildMergedSelection(
          existingSelections, category.yesOptions || [], category.noOptions || [], category.noOptions[0]
        );
        acc[categoryName] = {
          present: false,
          selectedOption: merged
        };
        return acc;
      }, {});
      return { ...prev, selections: newSelections };
    });

    const categoriesUpdates = {};
    Object.entries(questionPanels[panelIndex].categories).forEach(([categoryName, category]) => {
      const existingSelections = localData.selections[panelIndex]?.[categoryName]?.selectedOption || [];
      const merged = buildMergedSelection(
        existingSelections, category.yesOptions || [], category.noOptions || [], category.noOptions[0]
      );
      categoriesUpdates[categoryName] = {
        isPresentSelection: false,
        selectionDetails: merged.map(o => o.value)
      };
    });

    sendSectionUpdates({
      subsections: {
        [panelIndex + 1]: {
          categories: categoriesUpdates
        }
      }
    });

    setHasChanges(true);
  };

  const handleNoToAll = (panelIndex) => {
    if (!canEdit) return;
    executeNoToAll(panelIndex);
  };

  // Function to check if any risks are marked as "Yes"
  const getActiveRisks = useCallback(() => {
    if (!localData.selections) return [];
    
    const activeRisks = [];
    
    questionPanels.forEach((panel, panelIndex) => {
      Object.entries(panel.categories).forEach(([categoryName, category]) => {
        if (localData.selections[panelIndex]?.[categoryName]?.present === true) {
          activeRisks.push(categoryName);
        }
      });
    });
    
    return activeRisks;
  }, [localData.selections, questionPanels]);

  const activeRisks = useMemo(() => getActiveRisks(), [getActiveRisks]);
  const hasActiveRisks = activeRisks.length > 0;


  const handleSectionCompletion = useCallback((patientId, currentStageTitle, currentSectionTitle, hasChanges) => {
    if (!canEdit) return Promise.resolve();

    const currentSectionData = patientDataModel?.dataModel?.['2_clinicalInterview']
      ?.sections?.[currentSectionNumber];
    const isAlreadyCompleted = currentSectionData?.status === 'completed';

    const sectionsAlreadyGenerated = patientDataModel?.dataModel?.['6_manageReports']?.sections?.['1']?.stageSectionsGenerated;
    const isCurrentSectionGenerated = sectionsAlreadyGenerated?.includes(currentSectionTitle) || false;

    const needsStatusWrite = !isAlreadyCompleted;
    const needsGeneration = hasChanges || !isCurrentSectionGenerated;

    if (!needsStatusWrite && !needsGeneration) {
      return Promise.resolve();
    }

    if (needsStatusWrite) {
      sendSectionUpdates({ status: 'completed', progress: 100 });
    }

    const dataReady = flushPendingUpdates({ noTimeout: true });

    if (needsGeneration) {
      TriggerGenerateDocumentSection(patientId, currentStageTitle, currentSectionTitle, [], dataReady);
    }

    return dataReady;
  }, [sendSectionUpdates, patientDataModel, canEdit, flushPendingUpdates, currentSectionNumber]);

  // --- Deferred-DF CI completion state and handlers ---
  // When wf.isDFDeferred, RSK acts as the final CI section.  Clicking
  // "Finish Clinical Interview" generates all remaining CI paragraphs
  // (excluding DF) and shows a completion dialog before navigating to SSI.
  const [isCICompleteDialogOpen, setIsCICompleteDialogOpen] = useState(false);

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Set CI as completed in Firestore, record audit entry, and navigate to SSI
  const handleCICompletionNavigate = useCallback(async () => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['2_clinicalInterview']);

    await updatePatientFirestore(patientId, {
      '2_clinicalInterview': {
        status: 'completed',
        progress: 100,
        docs_available: true,
        lastCompletedAt: serverTimestamp(),
        lastCompletedBy: auth.currentUser.uid,
        lastCompletedByName: auth.currentUser.displayName,
        ...firstCompletedFields,
      },
      '6_manageReports': {
        ciReportGenerated: true,
        ciReportGeneratedAt: serverTimestamp(),
        ciReportGeneratedBy: auth.currentUser.uid,
        ciReportGeneratedByName: auth.currentUser.displayName,
        ...buildFirstGeneratedFields('ci', manageReports),
      }
    });

    appendReportAuditEntry(patientId, {
      module: 'ci',
      eventType: 'generated',
      isFirstOccurrence: !manageReports?.ciReportFirstGeneratedAt,
    });

    // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'ci',
      didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
      enabled: wf.isAppointmentDatesEnabled,
    });

    await delay(500);
    navigate(`/patient/${patientId}/proforma/semi-structured-interview/symptoms`);
  }, [patientId, updatePatientFirestore, navigate, patientDataModel, wf.isAppointmentDatesEnabled]);

  const handleFinishClinicalInterview = useCallback(async () => {
    if (!canEdit) return;
    markRegenHandled();
    // Await here because fetchPatientData needs committed data to read
    // back the latest stageSectionsGenerated for the bulk trigger check.
    await handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges);
    await fetchPatientData(patientId);

    // Generate AI paragraphs for all CI sections that haven't been generated yet,
    // deliberately excluding Diagnostic Formulation (which will be done later)
    // and Patient Information (which has no AI paragraph).
    const sectionsAlreadyGenerated = patientDataModel?.dataModel?.['6_manageReports']?.sections?.['1']?.stageSectionsGenerated || [];
    const visibleTitlesAll = getVisibleCISectionTitles(ciSections, excludedKeys);
    const sectionTitlesCI = visibleTitlesAll.filter(title =>
      title !== 'Patient Information' && title !== 'Diagnostic Formulation'
    );
    const sectionsNotGenerated = sectionTitlesCI.filter(title => !sectionsAlreadyGenerated.includes(title));

    if (sectionsNotGenerated.length > 0) {
      console.log('Generating remaining CI sections (excluding DF):', sectionsNotGenerated);
      // Data is already confirmed committed from the await above, so
      // pass a resolved Promise — no delay needed.
      sectionsNotGenerated.forEach(sectionTitle =>
        TriggerGenerateDocumentSection(patientId, currentStageTitle, sectionTitle, [], Promise.resolve())
      );
    }

    // No ci_DF stamp needed here.  When ci_DF is absent from outputs,
    // the backend template renderer removes the Formulation heading
    // (document_functions.py) and RD_CI.js filters it from the review
    // panel.  The deferred DF (4_DF.js) triggers generation later,
    // populating ci_DF with real content.  Stamping ci_DF: '' here
    // would destructively overwrite the real paragraph if the clinician
    // re-completes CI after the DF has already been done.

    setIsNavigating(false);

    // Re-entry guard (one-clinician SLaM): if the end-of-CI decision was
    // already recorded on an earlier visit, do not ask again.
    //   endAssessment -> the assessment is ended; the CI report (with the
    //                    Assessment Conclusion) already exists, so open its
    //                    review. Resuming is done from the Patient Overview.
    //   proceedToSsi  -> carry straight on to the SSI (re-affirming the CI
    //                    completion exactly as the plain dialog's CTA would).
    if (wf.allowEndAssessmentAfterCI && wf.ciEndDecision) {
      if (wf.ciEndDecision === CI_END_DECISIONS.END_ASSESSMENT) {
        navigate(`/patient/${patientId}/review-document-ci`);
        return;
      }
      if (wf.ciEndDecision === CI_END_DECISIONS.PROCEED_TO_SSI) {
        await handleCICompletionNavigate();
        return;
      }
    }

    setIsCICompleteDialogOpen(true);
  }, [canEdit, patientId, currentStageTitle, currentSectionTitle, hasChanges, handleSectionCompletion, fetchPatientData, patientDataModel, ciSections, excludedKeys, markRegenHandled, wf.allowEndAssessmentAfterCI, wf.ciEndDecision, navigate, handleCICompletionNavigate]);

  // --- One-clinician SLaM: end-of-CI decision handlers ---
  // True while either decision's Firestore writes are in flight (disables the
  // dialog controls so a double click cannot write twice).
  const [isEndDecisionSubmitting, setIsEndDecisionSubmitting] = useState(false);
  // True while the simulated CI report progress dialog runs after "End
  // assessment"; it then lands the clinician on the CI report review.
  const [isGeneratingEndReport, setIsGeneratingEndReport] = useState(false);

  /**
   * Option 1 — Proceed to Semi-Structured Interview. Records the decision on
   * workflowDecisions (outside dataModel, so it needs its own transaction
   * rather than updatePatientFirestore), then completes the CI and navigates
   * exactly as the plain completion dialog does. Recording "proceed" is what
   * unlocks the SSI card on the Patient Overview for this workflow.
   */
  const handleProceedToSsiDecision = useCallback(async () => {
    if (!canEdit || isEndDecisionSubmitting) return;
    setIsEndDecisionSubmitting(true);
    try {
      await runTransaction(db, async (transaction) => {
        transaction.update(doc(db, 'patients', patientId), buildProceedToSsiUpdates());
      });
      // Pull the decision into the local patient model BEFORE navigating.
      // The transaction above bypasses PatientDataContext, so without this
      // re-read the SSI pages would mount against a model whose
      // workflowDecisions still lack "proceedToSsi"; useSsiLockGuard would
      // then treat the SSI as locked and bounce the Lead straight back to
      // the overview (a race against the debounced completion write below).
      await fetchPatientData(patientId);
      setIsCICompleteDialogOpen(false);
      await handleCICompletionNavigate();
    } catch (error) {
      console.error('Failed to record the end-of-CI decision (proceedToSsi):', error);
      alert('Could not save your choice. Please try again.');
    } finally {
      setIsEndDecisionSubmitting(false);
    }
  }, [canEdit, isEndDecisionSubmitting, patientId, fetchPatientData, handleCICompletionNavigate]);

  /**
   * Option 2 — End assessment here. One transaction writes the decision, the
   * root assessmentEnd record (reason + optional next steps + who/when), CI
   * completion and the ciReport* generated fields (see utils/assessmentEnd.js).
   * Then:
   *   - audit: the usual CI 'generated' entry plus a distinct
   *     'assessmentEndedAfterCi' entry so the end is visible in the history;
   *   - inferred appointment date on first CI completion (same as proceed);
   *   - trigger the Assessment Conclusion paragraph, drafted by the backend
   *     from assessmentEnd.reason and assessmentEnd.nextSteps. The remaining
   *     CI paragraphs were already kicked off by handleFinishClinicalInterview
   *     before the dialog opened.
   *   - simulated progress dialog -> CI report review, where the new
   *     paragraph sits alongside the others for approval.
   */
  const handleEndAssessmentDecision = useCallback(async ({ reason, nextSteps }) => {
    if (!canEdit || isEndDecisionSubmitting) return;
    setIsEndDecisionSubmitting(true);
    try {
      const { updates, didWriteFirstCompleted, isFirstGeneration } = buildEndAssessmentUpdates({
        patientDataModel,
        user: auth.currentUser,
        reason,
        nextSteps,
      });

      await runTransaction(db, async (transaction) => {
        transaction.update(doc(db, 'patients', patientId), updates);
      });

      appendReportAuditEntry(patientId, {
        module: 'ci',
        eventType: 'generated',
        isFirstOccurrence: isFirstGeneration,
      });
      appendReportAuditEntry(patientId, {
        module: 'ci',
        eventType: ASSESSMENT_END_AUDIT_EVENTS.ENDED,
        isFirstOccurrence: false,
      });

      recordInferredAppointmentDateOnCompletion({
        patientId,
        moduleKey: 'ci',
        didWriteFirstCompleted,
        enabled: wf.isAppointmentDatesEnabled,
      });

      // The transaction has committed, so the backend can read the reason now.
      TriggerGenerateDocumentSection(
        patientId,
        currentStageTitle,
        ASSESSMENT_CONCLUSION_SECTION_TITLE,
        [],
        Promise.resolve(),
      );

      setIsCICompleteDialogOpen(false);
      setIsGeneratingEndReport(true);
    } catch (error) {
      console.error('Failed to end the assessment after the Clinical Interview:', error);
      alert('Could not end the assessment. Please try again.');
    } finally {
      setIsEndDecisionSubmitting(false);
    }
  }, [canEdit, isEndDecisionSubmitting, patientId, patientDataModel, currentStageTitle, wf.isAppointmentDatesEnabled]);

  const handleAfterEndReportGenerated = useCallback(() => {
    setIsGeneratingEndReport(false);
    navigate(`/patient/${patientId}/review-document-ci`);
  }, [navigate, patientId]);

  const handleNextSection = useCallback(async () => {
    markRegenHandled();
    // When DF is deferred, use the CI completion dialog flow (needs
    // spinner + await).  Otherwise navigate immediately.
    if (wf.isDFDeferred) {
      setIsNavigating(true);
      await handleFinishClinicalInterview();
      return;
    }
    handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges);
    navigate(`/patient/${patientId}${nextSectionLink}`);
  }, [patientId, navigate, nextSectionLink, hasChanges, handleSectionCompletion, wf.isDFDeferred, handleFinishClinicalInterview, markRegenHandled]);

  const handlePrevSection = useCallback(() => {
    markRegenHandled();
    handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges);
    navigate(`/patient/${patientId}${prevRoute}`);
  }, [patientId, navigate, prevRoute, hasChanges, handleSectionCompletion, markRegenHandled]);

  const navigateToSection = useCallback((index) => {
    markRegenHandled();
    handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges);
    const section = SECTIONS_CI[index];
    if (section.route) {
      navigate(`/patient/${patientId}${section.route}`);
    }
  }, [patientId, navigate, SECTIONS_CI, hasChanges, handleSectionCompletion, markRegenHandled]);


const renderNotesPanel = () => {
  return (
    <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6">
      <h3 className="font-bold text-lg mb-4">Additional Notes</h3>
      <p className="text-left text-gray-700 mb-4">
        Please add any additional comments, observations or relevant information about risk factors not covered above.
      </p>
      
      <div className="flex flex-col">
        <div className="flex gap-1 mb-2">
          <h4 className="text-gray-700 text-sm font-semibold">
            Clinical Notes
          </h4>
        </div>
        
        <DictationTextArea
          value={additionalNotes}
          onChange={(val) => handleAdditionalNotesChange(val)}
          onBlur={handleAdditionalNotesBlur}
          canEdit={canEdit}
          ariaLabel="Risk Assessment notes"
        />
      </div>
    </div>
  );
};

  const breadcrumbTuples = useMemo(() => [    
    ["Patient Overview", `/patient/${patientId}`],
    [currentStageTitle],
    [currentSectionTitle]
  ], [patientId, currentStageTitle, currentSectionTitle]);

  const styles = useMemo(() => ({
    menu: (base) => ({
      ...base,
      textAlign: 'left',
    }),
    option: (base, state) => ({
      ...base,
      textAlign: 'left',
      backgroundColor: state.isSelected ? 'lightgray' : base.backgroundColor,
    }),
  }), []);

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <div className="flex">
        <CISideNavbar
          sections={SECTIONS_CI}
          currentSectionIndex={currentSectionIndex}
          navigateToSection={navigateToSection}
          isNavigating={isNavigating}
          wf={wf}
        />
        <div className="flex-grow p-8">
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={handlePrevSection}
              disabled={isNavigating}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 ${isNavigating ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'}`}
              aria-label="Back to Previous Section"
            >
              {isNavigating
                ? <ButtonSpinner className="w-5 h-5 mr-2" />
                : <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />}
              <span className="font-bold">{isNavigating ? 'Loading...' : `Back to ${prevTitleComputed}`}</span>
            </button>
            {/* Finish CI button is only shown to the assigned clinician when DF is deferred;
                for non-deferred orgs the "Go to Diagnostic Formulation" nav button is
                always visible since it is just section navigation, not a completion action. */}
            {(!wf.isDFDeferred || canEdit) && (
            <button
              onClick={handleNextSection}
              disabled={isNavigating}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition duration-100 ml-auto ${isNavigating ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'hover:bg-violet-800 bg-violet-900 text-white'}`}
            >
              <span className="font-bold">
                {isNavigating
                  ? (wf.isDFDeferred ? 'Saving Progress...' : 'Loading...')
                  : (wf.isDFDeferred ? 'Finish Clinical Interview' : `Go to ${nextSectionTitle}`)}
              </span>
              {isNavigating
                ? <ButtonSpinner className="w-5 h-5 ml-2" />
                : (wf.isDFDeferred
                    ? <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />
                    : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />)
              }
            </button>
            )}
          </div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{currentSectionNumber}. {currentSectionTitle}</h2>
          </div>          

        {/* Risk Banner */}
        <div className={`overflow-hidden transition-all duration-400 ease-in-out ${hasActiveRisks ? 'max-h-96 mb-6 opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="bg-red-50 rounded-2xl p-4 text-left risk-banner">
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <ExclamationIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <h3 className="text-md font-medium text-red-800">For Consideration</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p className="mb-1">
                      You have selected "Yes" to the following risks: <span className="font-semibold">{activeRisks.join(', ')}</span>
                    </p>
                    <p className="mb-1">
                      Please consider a risk management plan for this patient.
                    </p>
                    <p className="mb-1">
                      Risk management functionality is not yet fully supported by the ThinkDivergent platform.
                    </p>
                    <p className="font-medium">
                      By continuing, you acknowledge this notice and take responsibility for appropriate clinical action.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Question panels */}
          {questionPanels.map((panel, panelIndex) => {
            // Disable the bulk action when every category in this panel is already No
            const categoryNames = Object.keys(panel.categories);
            const allAlreadyNo = categoryNames.length > 0 && categoryNames.every(
              (catName) => localData.selections[panelIndex]?.[catName]?.present === false
            );
            const noToAllDisabled = !canEdit || allAlreadyNo;

            return (
            <div key={panelIndex} className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6">
              <div className="flex justify-between items-center mb-4">
                <div className="w-1/3">
                  <h4 className="font-bold text-lg">{panel.title}</h4>
                </div>
                <div className="w-1/6 flex justify-center">
                    <button
                      onClick={() => handleNoToAll(panelIndex)}
                      className={`py-2 px-4 rounded-md shadow-md font-bold text-black bg-white border border-black transition-all duration-100 ${
                        noToAllDisabled
                          ? 'opacity-60 cursor-not-allowed'
                          : 'hover:bg-red-700 hover:text-white'
                      }`}
                      disabled={noToAllDisabled}
                    >
                      No to all?
                    </button>
                </div>
                <div className="w-1/2"></div>
              </div>
              <table className="w-full table-auto">
                <thead>
                  <tr>
                    <th className="text-left font-bold text-base w-1/3">Category</th>
                    <th className="text-center font-bold text-base w-1/6">Present?</th>
                    <th className="text-center font-bold text-base w-1/2">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(panel.categories).map(([categoryName, category]) => (
                    <tr key={categoryName} className="border-t">
                      <td className="py-2 font-bold text-sm">{categoryName}</td>
                      <td className="py-2 text-center">
                        <button
                          onClick={() => handleSelection(panelIndex, categoryName, true)}
                          className={`py-1 px-3 rounded-l-full border font-bold transition-all duration-300 ${localData.selections[panelIndex]?.[categoryName]?.present === true
                            ? 'bg-green-200 border-gray-600 border-2'
                            : 'bg-white border-gray-400'
                            }`}
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => handleSelection(panelIndex, categoryName, false)}
                          className={`py-1 px-3 rounded-r-full border font-bold transition-all duration-300 ${localData.selections[panelIndex]?.[categoryName]?.present === false
                            ? 'bg-red-200 border-gray-600 border-2'
                            : 'bg-white border-gray-400'
                            }`}
                        >
                          No
                        </button>
                      </td>
                      <td className="py-2 text-center">
                        <AutoSaveCreatable
                          isMulti={true}
                          isDisabled={!canEdit}
                          styles={styles}
                          options={(localData.selections[panelIndex]?.[categoryName]?.present ? category.yesOptions : category.noOptions).map(option => ({ value: option, label: option }))}
                          value={localData.selections[panelIndex]?.[categoryName]?.selectedOption || []}
                          onChange={(selectedOptions) => {
                            // Pre-defined options are mutually exclusive (only one allowed),
                            // but custom/user-created options can coexist alongside the chosen
                            // pre-defined option. This lets clinicians pick e.g. "Active with
                            // Plan but no Intent" AND add free-text qualifiers like
                            // "patient disclosed timeline" without losing the structured value.
                            const { predefined, custom } = splitPredefinedAndCustom(
                              selectedOptions, category.yesOptions, category.noOptions
                            );
                            // Follow the established noneOptions pattern (FormTemplate.js 411-418):
                            // when custom entries exist, drop the no-sentinel (e.g. "None") since
                            // it contradicts real clinician-typed detail.
                            const latestPredefined = custom.length > 0
                              ? predefined.filter(opt => !category.noOptions.includes(opt.value)).slice(-1)
                              : predefined.slice(-1);
                            handleOptionChange(panelIndex, categoryName, [...latestPredefined, ...custom]);
                          }}
                          formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                          placeholder={canEdit ? "Select an option and/or type your own..." : "Selection can only be made by assigned clinician"}
                          className="basic-multi-select text-sm" 
                          classNamePrefix="select"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          })}

          {renderNotesPanel()}

          <div className="flex justify-end items-center mt-8 pt-4 border-t border-gray-200">       
            {(!wf.isDFDeferred || canEdit) && (
            <button
              onClick={handleNextSection}
              disabled={isNavigating}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition duration-100 ml-auto ${isNavigating ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'hover:bg-violet-800 bg-violet-900 text-white'}`}
            >
              <span className="font-bold">
                {isNavigating
                  ? (wf.isDFDeferred ? 'Saving Progress...' : 'Loading...')
                  : (wf.isDFDeferred ? 'Finish Clinical Interview' : `Go to ${nextSectionTitle}`)}
              </span>
              {isNavigating
                ? <ButtonSpinner className="w-5 h-5 ml-2" />
                : (wf.isDFDeferred
                    ? <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />
                    : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />)
              }
            </button>
            )}
          </div>
        </div>      

      </div>

      {/* One-clinician SLaM: the end-of-CI decision replaces the plain
          completion dialog. The clinician either proceeds to the SSI or ends
          the assessment here (reason required, Assessment Conclusion paragraph
          generated into the CI report). */}
      {wf.isDFDeferred && wf.allowEndAssessmentAfterCI && (
        <>
          <CIEndDecisionDialog
            open={isCICompleteDialogOpen && !isGeneratingEndReport}
            onOpenChange={setIsCICompleteDialogOpen}
            isSubmitting={isEndDecisionSubmitting}
            onProceedToSsi={handleProceedToSsiDecision}
            onEndAssessment={handleEndAssessmentDecision}
          />
          <GenerateReportProgressDialog
            reportStage="Clinical Interview"
            patientId={patientId}
            clinicianId={auth.currentUser?.uid}
            handleNextPageButtonClick={handleAfterEndReportGenerated}
            isGenerating={isGeneratingEndReport}
            onCancel={() => setIsGeneratingEndReport(false)}
          />
        </>
      )}

      {/* CI completion dialog — shown when DF is deferred and the clinician
          finishes the Clinical Interview from the Risk Assessment page.
          The DF will be completed after the SSI instead. */}
      {wf.isDFDeferred && !wf.allowEndAssessmentAfterCI && (
        <ModuleCompletionDialog
          open={isCICompleteDialogOpen}
          onOpenChange={setIsCICompleteDialogOpen}
          title="Clinical Interview Complete"
          description={<>Your <span className="font-semibold">Formulation</span> will be completed after the <span className="font-semibold">Semi-Structured Interview</span>.</>}
          width={600}
          actions={
            <>
              <CancelButton onClick={() => setIsCICompleteDialogOpen(false)} />
              <PrimaryActionButton
                label="Go to Semi-Structured Interview"
                onClick={handleCICompletionNavigate}
              />
            </>
          }
        >
          <p className="mb-6 text-sm italic text-gray-700">
            All <span className="font-semibold">Clinical Interview</span> sections have been saved. You will now proceed to the <span className="font-semibold">Semi-Structured Interview</span>, at the end of which you will be able to record your <span className="font-semibold">Formulation</span>.
          </p>
        </ModuleCompletionDialog>
      )}

    </Layout>
  );
}

export default RiskAssessment;